import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { LeavePrompt } from '../Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles } from './InputTable.styles';
import { ErrorList, InputTableColumns, UpHeaderColumn } from './InputTable.type';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import CircularProgress from '@material-ui/core/CircularProgress';
import { isElementInContainer } from '../ExaminersStructure/helpers';
import _ from 'lodash';

interface InputTableProps<T> {
  columns: InputTableColumns[];
  parameters: T[];
  items: T[];
  keyParam: keyof T;
  invalidType: ErrorList[];
  headerText: string;
  onCancel: () => void;
  onSave: () => void;
  getValidationMessage: (object: ErrorList) => ReactNode;
  getRowComponent: (type: string, data: T, isValid: boolean) => React.ReactNode | string | null;
  edited: boolean;
  emptyMessage: string;
  checkRequired?: (item: ErrorList) => boolean;
  upHeaderColumns?: UpHeaderColumn[];
  childrenComponent?: ReactNode;
  containerHeight?: number | string;
  isNotRequiredMessage?: string;
  activeProcessRow?: keyof T;
  isLoading?: boolean;
  ariaLabel?: string;
}

const parametersItemsPagination = 30;

export const InputTable = <T,>({
  columns,
  parameters,
  getValidationMessage,
  getRowComponent,
  upHeaderColumns,
  emptyMessage,
  invalidType,
  headerText,
  edited,
  items,
  onCancel,
  onSave,
  keyParam,
  checkRequired,
  childrenComponent,
  containerHeight,
  isNotRequiredMessage,
  activeProcessRow,
  isLoading,
}: InputTableProps<T>): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();

  const [isDiscardOpen, setIsDiscardOpen] = useState<boolean>(false);
  const styleHeight = containerHeight !== undefined ? containerHeight : '410px';

  const [parametersRenderItemsCount, setParametersRenderItemsCount] = useState(parametersItemsPagination);
  const allItemsCount = useRef(parameters.length);
  const needToLoadMoreItemsContainerRef = useRef<HTMLTableSectionElement | null>(null);
  const needToLoadMoreItemsLoaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isLoading) {
      allItemsCount.current = parameters.length;
    } else {
      setParametersRenderItemsCount(parametersItemsPagination);
    }
  }, [isLoading, parameters.length]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    _.debounce(() => {
      if (
        needToLoadMoreItemsLoaderRef.current &&
        needToLoadMoreItemsContainerRef.current &&
        isElementInContainer(needToLoadMoreItemsLoaderRef.current, needToLoadMoreItemsContainerRef.current)
      ) {
        setParametersRenderItemsCount((prevValue) =>
          prevValue < allItemsCount.current ? prevValue + parametersItemsPagination : allItemsCount.current,
        );
      }
    }, 500),
    [],
  );

  const limitedParameters = useMemo(() => {
    return parameters.slice(0, parametersRenderItemsCount);
  }, [parameters, parametersRenderItemsCount]);

  return isLoading ? (
    <LinearProgress
      sx={{
        width: '100%',
        '& .MuiLinearProgress-colorPrimary': {
          color: 'rgba(202, 202, 252, 0.3)',
        },
        '& .MuiLinearProgress-barColorPrimary': {
          backgroundColor: '#3C419A',
        },
      }}
    />
  ) : (
    <Grid container direction="column" className={styles.root}>
      <h4 className={styles.header}>{headerText}</h4>
      <div>
        {childrenComponent !== undefined && childrenComponent}
        <Paper elevation={0}>
          <TableContainer style={{ height: styleHeight }} onScroll={handleScroll} ref={needToLoadMoreItemsContainerRef}>
            <Table stickyHeader aria-label={headerText}>
              <TableHead>
                {upHeaderColumns && upHeaderColumns.length && (
                  <TableRow>
                    <TableCell
                      classes={{
                        root: styles.headerCell,
                      }}
                      align="center"
                      colSpan={1}
                    >
                      Nazwa grupy zadań
                    </TableCell>
                    {upHeaderColumns.map((e, index) => (
                      <TableCell
                        classes={{
                          root: styles.headerCell,
                        }}
                        align="center"
                        colSpan={5}
                        style={{ ...(e.style ? e.style : null) }}
                        key={`inputTableKey ${e.id} ${index}`}
                      >
                        {e?.element ?? e?.title}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
                <TableRow>
                  {columns.map((column) => (
                    <TooltipOne title={column.tooltipText ?? ''} placement="top" key={`to ${column.id}`}>
                      <TableCell
                        key={column.id}
                        align="left"
                        classes={{
                          root: styles.headerCell,
                        }}
                        style={{
                          ...(column.style ? column.style : null),
                        }}
                      >
                        {column.label}
                      </TableCell>
                    </TooltipOne>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!parameters.length && !isNotRequiredMessage && (
                  <TableRow>
                    <TableCell align="center" colSpan={columns.length}>
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
                {limitedParameters.map((item) => {
                  const isInvalidRow = invalidType.filter((it) => it.id === (item[keyParam] as unknown)).pop();
                  const isRequired = checkRequired && isInvalidRow ? checkRequired(isInvalidRow) : false;
                  const key = `inputTableKey ${item[keyParam]}`;
                  const isActiveRow = activeProcessRow && item[activeProcessRow] ? item[activeProcessRow] : false;
                  return (
                    <React.Fragment key={key}>
                      <TableRow tabIndex={-1}>
                        {columns.map((column, _index) => {
                          const rowCell: React.ReactNode = getRowComponent(
                            column.id,
                            item,
                            (isInvalidRow &&
                              `inputTableKey ${isInvalidRow.id}` === key &&
                              isInvalidRow.type === column.id) ??
                              false,
                          );

                          const colSpan =
                            _index === 1 && activeProcessRow && !isActiveRow ? columns.length - 1 : undefined;

                          return (
                            <TableCell
                              colSpan={colSpan}
                              key={`${column.id}-${_index}`}
                              classes={{
                                root: [styles.row, isInvalidRow && !isRequired ? styles.invalidRow : ''].join(' '),
                              }}
                              style={{
                                ...(column.style ? column.style : null),
                              }}
                            >
                              {rowCell}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      {isInvalidRow && !isRequired && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length}
                            classes={{
                              root: styles.invalidRowCell,
                            }}
                          >
                            {getValidationMessage(isInvalidRow)}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {allItemsCount.current > parametersRenderItemsCount && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <div ref={needToLoadMoreItemsLoaderRef}>
                        <CircularProgress size={20} onClick={handleScroll} />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {isNotRequiredMessage && isNotRequiredMessage.length && (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      classes={{
                        root: styles.infoRowCell,
                      }}
                    >
                      <InfoOutlinedIcon sx={{ color: '#3560FF', fontSize: 18, marginRight: 2 }} />
                      {isNotRequiredMessage}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <div className={styles.actions}>
            <ButtonOne variant="outlined" color="primary" onClick={edited ? () => setIsDiscardOpen(true) : onCancel}>
              Anuluj
            </ButtonOne>
            <ButtonOne
              variant="contained"
              color="primary"
              disabled={invalidType.length > 0 || !items.length || !edited}
              onClick={onSave}
            >
              Zapisz
            </ButtonOne>
          </div>
        </Paper>
      </div>
      <LeavePrompt isOpen={isDiscardOpen} onConfirm={onCancel} onCancel={() => setIsDiscardOpen(false)} />
    </Grid>
  );
};
